import { E, wsClient } from '../logic/imports';

/**
 * Manages tracked instruments for quote streaming.
 * Handles debouncing, deduplication, and sending subscription updates to the server.
 */
class QuoteSubscriptionManager {
    private trackedSymbols: string[] = [];
    private lastSentSymbols: string[] = [];
    private setTrackedSymbolsTimer: NodeJS.Timeout | null = null;

    constructor() {}

    /**
     * Add an instrument to track. If already tracked, does nothing.
     */
    addTrackedSymbol(symbol: string): void {
        // Check if instrument already exists (by symbol and type)
        const exists = this.trackedSymbols.includes(symbol);
        
        if (!exists) {
            this.trackedSymbols.push(symbol);
            this.scheduleUpdate();
        }
    }

    /**
     * Remove an instrument from tracking by symbol.
     */
    removeTrackedSymbol(symbol: string): void {
        // Find and remove the matching instrument
        const index = this.trackedSymbols.findIndex(
            s => s === symbol
        );
        
        if (index !== -1) {
            this.trackedSymbols.splice(index, 1);
            this.scheduleUpdate();
        } else {
            console.log('[QSM] Tracked symbol not found:', symbol);
        }
    }

    /**
     * Clear all tracked symbols.
     */
    clearTrackedSymbols(): void {
        this.trackedSymbols = [];
    }

    /**
     * Get the current list of tracked symbols.
     */
    getTrackedSymbols(): string[] {
        return [...this.trackedSymbols];
    }

    /**
     * Check if two symbol arrays are equivalent.
     */
    private symbolsAreEqual(a: string[], b: string[]): boolean {
        if (a.length !== b.length) return false;
        
        // Create a sorted copy of each array for comparison
        const sortedA = [...a].sort((x, y) => x.localeCompare(y));
        const sortedB = [...b].sort((x, y) => x.localeCompare(y));
        
        return sortedA.every((inst, i) => inst === sortedB[i]);
    }

    /**
     * Schedule an update to be sent to the server.
     * Debounced to batch multiple rapid calls into a single send.
     */
    private scheduleUpdate(): void {
        // Clear existing timer if one is pending
        if (this.setTrackedSymbolsTimer) {
            clearTimeout(this.setTrackedSymbolsTimer);
        }

        // Set new timer - delay execution by 50ms to allow batching of add/remove calls
        this.setTrackedSymbolsTimer = setTimeout(() => {
            this.sendUpdate();
            this.setTrackedSymbolsTimer = null;
        }, 50);
    }

    /**
     * Send the current tracked instruments to the server.
     * Only sends if instruments have changed from the last sent set.
     */
    private sendUpdate(): void {
        // Check if WebSocket is open before attempting to send
        if (!wsClient.ws || wsClient.ws.readyState !== WebSocket.OPEN) {
            // console.log('[QSM] WebSocket not open, skipping send');
            return;
        }

        // Check if instruments have actually changed
        if (this.symbolsAreEqual(this.trackedSymbols, this.lastSentSymbols)) {
            // console.log('[QSM] Tracked instruments unchanged, skipping send');
            return;
        }

        // console.log('[QSM] Sending tracked instruments:', this.trackedInstruments.map(i => i.symbol).join(', '));

        try {
            wsClient.sendWsMessage(
                E.WsRoute.QUOTES,
                E.WsQuoteCommand.STREAM_INSTRUMENTS,
                this.trackedSymbols
            );

            // Update the last sent set
            this.lastSentSymbols = [...this.trackedSymbols];
        } catch (error) {
            console.error('[QSM] Failed to send tracked instruments:', error);
        }
    }

    /**
     * Force send tracked symbols to server (e.g., after WebSocket reconnects).
     * Call this when the WebSocket connection is established.
     */
    onConnectionEstablished(): void {
        if (this.trackedSymbols.length > 0) {
            // Reset lastSentSymbols to force a send
            this.lastSentSymbols = [];
            this.sendUpdate();
        }
    }
}

export const quoteSubscriptionManager = new QuoteSubscriptionManager();
